
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { QuoteIcon } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { usePublishedReviews } from "@/hooks/usePublishedReviews";

const Testimonials = () => {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { data: reviews = [], isLoading } = usePublishedReviews(4);

  // Avoid a thin/empty section on the homepage
  if (!isLoading && reviews.length < 2) return null;

  return (
    <section 
      ref={ref}
      className={`py-16 bg-brand-100/60 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      data-testid="testimonials"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-brand text-warm-800 mb-4">
            {t('healingStories')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {isLoading
            ? [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-48 rounded-lg bg-white/40 border border-white/60 animate-pulse"
                />
              ))
            : reviews.map((review, index) => {
                const authorLabel = t('verifiedClient');
                const initials = authorLabel
                  .replace(/[^\p{L}\p{N} ]/gu, '')
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join('')
                  .toUpperCase();

                return (
                  <Card
                    key={review.id}
                    className={`bg-white/60 backdrop-blur-xl border border-white/80 shadow-lg card-hover transition-all duration-500 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${index * 150}ms` }}
                  >
                    <CardContent className="pt-6 relative">
                      <QuoteIcon className="h-8 w-8 text-brand-200 absolute top-6 right-6" />
                      <p className="text-warm-600 leading-relaxed relative z-10">{review.comment}</p>
                    </CardContent>
                    <CardFooter className="flex items-center pt-2 pb-6">
                      <Avatar className="h-10 w-10 mr-3 flex-shrink-0">
                        <AvatarFallback>{initials || 'VC'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-warm-800">{authorLabel}</p>
                        {review.practitionerName && (
                          <p className="text-sm text-warm-600">
                            {review.practitionerId ? (
                              <Link
                                to={`/book-an-appointment/${review.practitionerId}`}
                                className="hover:underline"
                              >
                                {t('sessionWith', { name: review.practitionerName })}
                              </Link>
                            ) : (
                              t('sessionWith', { name: review.practitionerName })
                            )}
                          </p>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                );
              })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
